/**
 * AgentOsDemoBehaviorAnalytics.jsx — sandbox for Behavior Analytics Agent.
 *
 * Lets the user paste in (or load preset) weekly session aggregates and
 * see the AI's prioritised fix recommendations + health score.
 */
import { useState } from "react";
import axios from "axios";
import { API } from "../config";
import { Sparkles, Loader2, BarChart3, AlertTriangle, TrendingUp, Lightbulb } from "lucide-react";
import AgentOsDemoShell from "./AgentOsDemoShell";

const EXAMPLES = [
  { page_url: "https://eshop.sk/produkt/aurora-hodinky", sessions_count: 1245, avg_time_on_page_s: 18, bounce_rate_pct: 72, rage_clicks: 8, dead_clicks: 3, scroll_depth_avg_pct: 42, form_abandonment_pct: 55, top_exit_element: "#checkout-form", cta_ctr_pct: 2.1 },
  { page_url: "https://eshop.sk/", sessions_count: 8420, avg_time_on_page_s: 52, bounce_rate_pct: 38, rage_clicks: 1, dead_clicks: 12, scroll_depth_avg_pct: 68, form_abandonment_pct: 18, top_exit_element: "main-nav", cta_ctr_pct: 6.8 },
  { page_url: "https://eshop.sk/checkout", sessions_count: 320, avg_time_on_page_s: 144, bounce_rate_pct: 28, rage_clicks: 12, dead_clicks: 2, scroll_depth_avg_pct: 85, form_abandonment_pct: 78, top_exit_element: "#payment-card", cta_ctr_pct: 1.2 },
];

export default function AgentOsDemoBehaviorAnalytics() {
  const [form, setForm] = useState({
    page_url: "", sessions_count: 100, avg_time_on_page_s: 30, bounce_rate_pct: 50,
    rage_clicks: 0, dead_clicks: 0, scroll_depth_avg_pct: 50, form_abandonment_pct: 0,
    top_exit_element: "", cta_ctr_pct: 3, lang: "sk",
  });
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState({ loading: false, error: null });

  const loadExample = (i) => { setForm((f) => ({ ...f, ...EXAMPLES[i] })); setResult(null); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.page_url.trim()) return;
    setStatus({ loading: true, error: null }); setResult(null);
    try {
      const r = await axios.post(`${API}/agent-os/demo/behavior-analytics`, form);
      setResult(r.data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setStatus({ loading: false, error: typeof d === "object" ? d : { message: d || "Skús to ešte raz." } });
      return;
    }
    setStatus({ loading: false, error: null });
  };

  const sevColor = { high: "text-red-400 border-red-700/40 bg-red-900/20", medium: "text-amber-400 border-amber-700/40 bg-amber-900/20", low: "text-emerald-400 border-emerald-700/40 bg-emerald-900/20" };

  const Left = (
    <form onSubmit={submit} className="p-6 bg-black border border-gray-800" data-testid="behavior-analytics-form">
      <div className="flex gap-2 mb-5 flex-wrap">
        {EXAMPLES.map((_, i) => (
          <button key={i} type="button" onClick={() => loadExample(i)} data-testid={`behavior-analytics-example-${i}`}
            className="text-[10px] px-3 py-1 border border-emerald-700/40 text-emerald-300 hover:bg-emerald-900/20 font-bold uppercase tracking-widest">
            {["Produkt", "Homepage", "Checkout"][i]}
          </button>
        ))}
      </div>
      <Field label="URL stránky"><input required value={form.page_url} maxLength={500}
        onChange={(e) => setForm({ ...form, page_url: e.target.value })}
        data-testid="behavior-analytics-input-url"
        placeholder="https://eshop.sk/produkt/..."
        className="w-full px-4 py-2.5 bg-neutral-950 border border-gray-800 text-white text-sm focus:border-emerald-500 outline-none" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <NumField label="Sessions / týždeň" value={form.sessions_count} onChange={(v) => setForm({ ...form, sessions_count: v })} testid="behavior-analytics-input-sessions" />
        <NumField label="Priem. čas (s)" value={form.avg_time_on_page_s} onChange={(v) => setForm({ ...form, avg_time_on_page_s: v })} testid="behavior-analytics-input-time" />
        <SliderField label={`Bounce rate ${form.bounce_rate_pct.toFixed(0)}%`} value={form.bounce_rate_pct} max={100}
          onChange={(v) => setForm({ ...form, bounce_rate_pct: v })} testid="behavior-analytics-input-bounce" />
        <SliderField label={`Scroll depth ${form.scroll_depth_avg_pct.toFixed(0)}%`} value={form.scroll_depth_avg_pct} max={100}
          onChange={(v) => setForm({ ...form, scroll_depth_avg_pct: v })} testid="behavior-analytics-input-scroll" />
        <NumField label="Rage clicks (events)" value={form.rage_clicks} onChange={(v) => setForm({ ...form, rage_clicks: v })} testid="behavior-analytics-input-rage" />
        <NumField label="Dead clicks (events)" value={form.dead_clicks} onChange={(v) => setForm({ ...form, dead_clicks: v })} testid="behavior-analytics-input-dead" />
        <SliderField label={`Form abandon ${form.form_abandonment_pct.toFixed(0)}%`} value={form.form_abandonment_pct} max={100}
          onChange={(v) => setForm({ ...form, form_abandonment_pct: v })} testid="behavior-analytics-input-formabandon" />
        <SliderField label={`CTA CTR ${form.cta_ctr_pct.toFixed(1)}%`} value={form.cta_ctr_pct} max={20} step={0.1}
          onChange={(v) => setForm({ ...form, cta_ctr_pct: v })} testid="behavior-analytics-input-ctr" />
      </div>
      <Field label="Top exit element"><input value={form.top_exit_element} maxLength={200}
        onChange={(e) => setForm({ ...form, top_exit_element: e.target.value })}
        placeholder="#checkout-form alebo .add-to-cart" data-testid="behavior-analytics-input-exit"
        className="w-full px-4 py-2.5 bg-neutral-950 border border-gray-800 text-white text-sm focus:border-emerald-500 outline-none" /></Field>
      <button type="submit" disabled={status.loading || !form.page_url}
        data-testid="behavior-analytics-submit"
        className="w-full mt-2 py-3 bg-emerald-400 hover:bg-emerald-300 disabled:bg-gray-700 disabled:text-gray-400 text-black font-black uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2">
        {status.loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyzujem…</> : <><Sparkles className="w-4 h-4" /> Analyzuj správanie</>}
      </button>
    </form>
  );

  const Right = (
    <div className="p-6 bg-black border border-gray-800" data-testid="behavior-analytics-output">
      {!result && !status.loading && !status.error && <Placeholder Icon={BarChart3} text="AI analyzuje user behavior a navrhne 3-5 konkrétnych opráv" />}
      {status.loading && <Loading text="Krížová analýza patternov, hľadám fix opportunity…" />}
      {status.error && <ErrorBox err={status.error} />}
      {result?.output && (
        <div className="space-y-4 text-sm">
          <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-emerald-900/30 to-black border border-emerald-700/40">
            <div className="text-center">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Health score</p>
              <p className="text-4xl font-black text-emerald-300 tabular-nums" data-testid="behavior-analytics-output-health">{result.output.overall_health_score}</p>
              <p className="text-[10px] text-gray-500 font-bold">/ 100</p>
            </div>
            <p className="text-gray-200 leading-relaxed text-xs" data-testid="behavior-analytics-output-summary">{result.output.executive_summary}</p>
          </div>
          {result.output.detected_issues?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-amber-400 font-bold mb-2 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Detekované issues</p>
              <div className="space-y-2">
                {result.output.detected_issues.map((iss, i) => (
                  <div key={i} className={`p-2 border ${sevColor[iss.severity] || sevColor.medium}`}>
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-black uppercase tracking-widest">{iss.code}</span>
                      <span className="text-[10px] font-bold">{iss.metric}</span>
                    </div>
                    <p className="text-xs text-gray-300 mt-1">{iss.hypothesis}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="pt-3 border-t border-gray-900">
            <p className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold mb-2 flex items-center gap-1"><Lightbulb className="w-3 h-3" /> Odporúčania</p>
            <div className="space-y-3" data-testid="behavior-analytics-output-recommendations">
              {(result.output.recommendations || []).map((rec, i) => (
                <div key={i} className="p-3 bg-neutral-950 border border-gray-900">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-black text-white">{rec.title}</p>
                    <div className="flex gap-2 shrink-0">
                      <span className={`text-[10px] px-2 py-0.5 font-bold uppercase tracking-widest ${rec.priority === "high" ? "bg-red-900/40 text-red-300 border border-red-700/40" : rec.priority === "medium" ? "bg-amber-900/40 text-amber-300 border border-amber-700/40" : "bg-gray-900 text-gray-400 border border-gray-700"}`}>
                        {rec.priority}
                      </span>
                      <span className="text-[10px] px-2 py-0.5 bg-emerald-900/40 text-emerald-300 border border-emerald-700/40 font-bold uppercase tracking-widest inline-flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" /> {rec.expected_lift}
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-300 leading-relaxed">{rec.action}</p>
                </div>
              ))}
            </div>
          </div>
          <p className="text-[10px] text-gray-600 pt-3 border-t border-gray-900">Zostáva {result.remaining_today} demo runov. Latencia: {result.latency_ms}ms.</p>
        </div>
      )}
    </div>
  );

  return <AgentOsDemoShell testid="agent-os-demo-behavior-analytics" h1="Behavior Analytics" lead="Heatmap-level insight + AI návrhy opráv. Týždenná analýza, prioritizované odporúčania s expected lift." left={Left} right={Right} />;
}

const Field = ({ label, children }) => (<label className="block mb-3"><span className="block text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1.5">{label}</span>{children}</label>);
const NumField = ({ label, value, onChange, testid }) => (
  <label className="block mb-3"><span className="block text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1.5">{label}</span>
    <input type="number" min="0" value={value} onChange={(e) => onChange(parseInt(e.target.value) || 0)} data-testid={testid}
      className="w-full px-3 py-2 bg-neutral-950 border border-gray-800 text-white text-sm focus:border-emerald-500 outline-none" /></label>
);
const SliderField = ({ label, value, max, step = 1, onChange, testid }) => (
  <label className="block mb-3"><span className="block text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1.5">{label}</span>
    <input type="range" min="0" max={max} step={step} value={value} onChange={(e) => onChange(parseFloat(e.target.value) || 0)} data-testid={testid}
      className="w-full" /></label>
);
const Placeholder = ({ Icon, text }) => (<div className="text-center py-10"><Icon className="w-10 h-10 text-gray-700 mx-auto mb-3" /><p className="text-sm text-gray-500">{text}</p></div>);
const Loading = ({ text }) => (<div className="text-center py-10"><Loader2 className="w-10 h-10 text-emerald-400 animate-spin mx-auto mb-3" /><p className="text-sm text-gray-400">{text}</p></div>);
const ErrorBox = ({ err }) => (<div className="p-4 border border-red-900/60 bg-red-950/30 text-sm text-red-300" data-testid="behavior-analytics-error">{err.message || "Skús to znova."}</div>);
